import React, { useEffect, useRef, useState } from 'react';
import { useGoogleMap } from '@react-google-maps/api';

/**
 * AdvancedMarker component - uses Google Maps AdvancedMarkerElement API
 * Provides backward compatibility with old Marker API
 */
const AdvancedMarker = React.forwardRef((props, ref) => {
    const {
        position,
        icon,
        onClick,
        draggable,
        onDragEnd,
        zIndex,
        opacity,
        children,
        clusterer,
        ...otherProps
    } = props;

    const map = useGoogleMap();
    const markerRef = useRef(null);
    const [marker, setMarker] = useState(null);

    useEffect(() => {
        if (!map || !window.google?.maps?.marker?.AdvancedMarkerElement) {
            return;
        }

        // Create marker content from icon
        let content = null;
        if (icon?.url) {
            const img = document.createElement('img');
            img.src = icon.url;
            if (icon.scaledSize) {
                img.style.width = `${icon.scaledSize.width}px`;
                img.style.height = `${icon.scaledSize.height}px`;
            }
            content = img;
        }

        // Create AdvancedMarkerElement
        const advancedMarker = new window.google.maps.marker.AdvancedMarkerElement({
            map: map,
            position: position,
            content: content,
            gmpDraggable: draggable || false,
            zIndex: zIndex,
            ...otherProps
        });

        // Apply opacity if specified
        if (opacity !== undefined && advancedMarker.element) {
            advancedMarker.element.style.opacity = opacity;
        }

        // Add click listener
        if (onClick) {
            advancedMarker.addListener('click', onClick);
        }

        // Add drag end listener
        if (onDragEnd) {
            advancedMarker.addListener('dragend', (e) => {
                onDragEnd({
                    latLng: e.latLng || advancedMarker.position
                });
            });
        }

        // Add legacy method shims for MarkerClusterer compatibility
        if (clusterer) {
            let _settingMap = false;
            advancedMarker.getDraggable = () => advancedMarker.gmpDraggable || false;
            advancedMarker.getPosition = () => new window.google.maps.LatLng(advancedMarker.position.lat, advancedMarker.position.lng);
            advancedMarker.getMap = () => advancedMarker.map;
            advancedMarker.setMap = (m) => {
                if (_settingMap) return;
                _settingMap = true;
                advancedMarker.map = m;
                _settingMap = false;
            };
            advancedMarker.getVisible = () => advancedMarker.map !== null;
            advancedMarker.setVisible = (v) => {
                if (advancedMarker.element) advancedMarker.element.style.display = v ? '' : 'none';
            };
            clusterer.addMarker(advancedMarker);
        }

        markerRef.current = advancedMarker;
        setMarker(advancedMarker);

        // Expose marker instance via ref
        if (ref) {
            if (typeof ref === 'function') {
                ref(advancedMarker);
            } else {
                ref.current = advancedMarker;
            }
        }

        // Cleanup
        return () => {
            if (clusterer) {
                clusterer.removeMarker(advancedMarker);
            }
            window.google.maps.event.clearInstanceListeners(advancedMarker);
            advancedMarker.map = null;
        };
    }, [map, position?.lat, position?.lng, icon?.url, draggable, zIndex, opacity, clusterer]);

    // Handle position updates
    useEffect(() => {
        if (marker && position) {
            marker.position = position;
        }
    }, [marker, position?.lat, position?.lng]);

    // Render children (InfoWindow) if provided, pass marker as anchor
    if (!children) return null;

    // Clone children and inject anchor prop for InfoWindow compatibility
    const childrenWithProps = React.Children.map(children, child => {
        if (React.isValidElement(child) && marker) {
            // Pass the marker instance as anchor to InfoWindow components
            return React.cloneElement(child, { anchor: marker });
        }
        return child;
    });

    return <>{childrenWithProps}</>;
});

AdvancedMarker.displayName = 'AdvancedMarker';

export default AdvancedMarker;
